import {
  ADMINS,
  APPROVER_ROLES,
  BOOKING_APPROVERS,
  FINANCE_STAFF_WITH_ADMINS,
  LEADERSHIP_VIEWERS,
  PAYMENT_REPORT_VIEWERS,
  REFUND_REQUESTERS,
  REPORT_VIEWERS,
  ROLES,
  SALE_CREATORS,
  SALES_RECORDERS,
  roleKey,
} from "../constants/roles";

/**
 * Derive unique companies with their roles from the login user payload.
 * user.companies / user.roles include pivot rows (one per company_user_role).
 */
export function getCompaniesWithRoles(user) {
  if (!user) return [];

  const companiesById = new Map();
  (user.companies || []).forEach((company) => {
    if (!company?.id) return;
    if (!companiesById.has(company.id)) {
      companiesById.set(company.id, {
        company: { id: company.id, name: company.name },
        roles: [],
      });
    }
  });

  (user.roles || []).forEach((role) => {
    const companyId = role?.pivot?.company_id;
    if (!companyId) return;

    if (!companiesById.has(companyId)) {
      const companyMatch = (user.companies || []).find((c) => c.id === companyId);
      companiesById.set(companyId, {
        company: {
          id: companyId,
          name: companyMatch?.name || `Company #${companyId}`,
        },
        roles: [],
      });
    }

    const entry = companiesById.get(companyId);
    if (!entry.roles.some((r) => r.id === role.id)) {
      entry.roles.push({ id: role.id, name: role.name });
    }
  });

  return Array.from(companiesById.values());
}

export function roleFlagsFromRoles(roles = []) {
  const names = roles.map((r) => roleKey(r.name));
  const has = (...wanted) =>
    names.some((n) => wanted.some((role) => roleKey(role) === n));

  const isSuperadmin = has(ROLES.SUPERADMIN);
  // Superadmin has everything Admin has, plus the Superadmin-only actions.
  const isAdmin = has(...ADMINS);
  const isManager = isAdmin || has(ROLES.MANAGER);
  const isInvestor = has(ROLES.INVESTOR);
  const isMarketingOfficer = has(ROLES.MARKETING_OFFICER);
  const isMarketingManager = has(ROLES.MARKETING_MANAGER);
  const isDirector = has(ROLES.EXECUTIVE_DIRECTOR);
  const isChairman = has(ROLES.CHAIRMAN);
  const isFinanceDirector = has(ROLES.FINANCE_DIRECTOR);
  const isGeneralUser = has(ROLES.USER);
  const isMd = has(ROLES.MD);
  const isApprover = has(...APPROVER_ROLES);

  return {
    isSuperadmin,
    isAdmin,
    isManager,
    isInvestor,
    isMarketingOfficer,
    isMarketingManager,
    isDirector,
    isChairman,
    isFinanceDirector,
    isGeneralUser,
    isMd,
    isApprover,
    // Page-level access, mirroring the backend role middleware.
    canApproveBookings: has(...BOOKING_APPROVERS),
    canRecordSales: has(...SALES_RECORDERS),
    canCreateSales: has(...SALE_CREATORS),
    canViewReports: has(...REPORT_VIEWERS),
    canRequestRefunds: has(...REFUND_REQUESTERS),
    canViewPaymentReports: has(...PAYMENT_REPORT_VIEWERS),
    canManageFinance: has(...FINANCE_STAFF_WITH_ADMINS),
    // One shared Leadership dashboard for Chairman, MD, Executive Director and Investor.
    hasLeadershipDashboard: has(...LEADERSHIP_VIEWERS),
  };
}
